import { Column, Header, HeaderGroup, Table } from '@tanstack/react-table';
import { sortBy } from 'lodash';
import React, { useCallback, useState } from 'react';

import { ColumnAdditionalData, ICssClasses } from '../../types';
import { SELECTION_COLUMN_ID } from '../../utils/makeRowsSelectable';

import {
  HeaderCellV8,
  HeaderCellV8Props,
} from './components/header-cell/HeaderCellV8';
import {
  DraggableColumnHeaderWrapper,
  IDraggableColumnDndItem,
} from './components/header-cell-dnd-wrapper/HeaderCellDndWrapper';
import { HeaderRow } from './components/header-row/HeaderRow';
import { HeaderWrapperV8 } from './components/header-wrapper/HeaderWrapperV8';

interface TableHeaderV8Props {
  headerGroups: HeaderGroup<ColumnAdditionalData>[];
  setColumnOrder: Table<unknown>['setColumnOrder'];
  flatColumns: Column<unknown, unknown>[];
  classes?: ICssClasses;
  width: number;
  styles?: {
    row?: React.CSSProperties;
    cell?: React.CSSProperties;
  };
  noSeperator?: boolean;
  groupBy: string[];
  additionalHeaders?: React.ReactElement<any> | null;
  additionalHeadersAfter?: React.ReactElement<any> | null;
  hideHeaderTooltip?: boolean;
}

export function TableHeaderV8(props: TableHeaderV8Props) {
  const {
    flatColumns,
    setColumnOrder,
    additionalHeaders,
    additionalHeadersAfter,
    hideHeaderTooltip,
    groupBy,
  } = props;
  const onDragEnd = useCallback(
    (item: IDraggableColumnDndItem) => {
      const column = flatColumns.find((o) => o.id === item.id);

      if (!column) return;

      const columns = sortBy(flatColumns, (column) => column.getIndex());
      const columnIds = columns.map((column) => column.id);
      const originalIndex = column.getIndex();

      if (item.index === originalIndex) {
        return;
      }

      // Get the dragged column to check if it's pinned/sticky
      const draggedColumn = column;

      // Separate columns by pinning type
      const leftPinnedColumns = columns.filter(
        (col) => col.getIsPinned() === 'left'
      );
      const normalColumns = columns.filter((col) => !col.getIsPinned());

      // Calculate the boundaries for each group
      const leftPinnedEnd = leftPinnedColumns.length - 1;
      const normalStart = leftPinnedColumns.length;
      const normalEnd = normalStart + normalColumns.length - 1;
      const rightPinnedStart = normalStart + normalColumns.length;

      // Restrict movement to within the same pinned group
      if (draggedColumn.getIsPinned() === 'left') {
        // Left pinned columns can only move within left pinned area (0 to leftPinnedEnd)
        if (item.index > leftPinnedEnd) {
          item.index = leftPinnedEnd;
        }
      } else if (draggedColumn.getIsPinned() === 'right') {
        // Right pinned columns can only move within right pinned area (rightPinnedStart to end)
        if (item.index < rightPinnedStart) {
          item.index = rightPinnedStart;
        }
      } else {
        // Normal columns can only move within normal area (normalStart to normalEnd)
        if (item.index < normalStart) {
          item.index = normalStart;
        } else if (item.index > normalEnd) {
          item.index = normalEnd;
        }
      }

      const adjust = item.index > originalIndex ? 0 : 1;

      columnIds.splice(item.index, 0, item.id);
      columnIds.splice(originalIndex + adjust, 1);

      setColumnOrder(columnIds);
    },
    [setColumnOrder, flatColumns]
  );

  const [dragState, setDragState] = useState<number | null>(null);

  // Helper function to check if a drop is allowed at a given index
  const isDropAllowed = useCallback(
    (draggedColumnId: string, targetIndex: number) => {
      const draggedColumn = flatColumns.find(
        (col) => col.id === draggedColumnId
      );
      if (!draggedColumn) return false;

      // Separate columns by pinning type (V8 uses getIsPinned())
      const leftPinnedColumns = flatColumns.filter(
        (col) => col.getIsPinned() === 'left'
      );
      const normalColumns = flatColumns.filter((col) => !col.getIsPinned());

      // Calculate the boundaries for each group
      const leftPinnedEnd = leftPinnedColumns.length - 1;
      const normalStart = leftPinnedColumns.length;
      const normalEnd = normalStart + normalColumns.length - 1;

      // Check if the drop is within the allowed zone for this column type
      if (draggedColumn.getIsPinned() === 'left') {
        // Left pinned columns can only be dropped in left pinned area (0 to leftPinnedEnd)
        return targetIndex >= 0 && targetIndex <= leftPinnedEnd;
      } else {
        // Normal columns can only be dropped in normal area (normalStart to normalEnd)
        return targetIndex >= normalStart && targetIndex <= normalEnd;
      }
    },
    [flatColumns]
  );

  const canReorderColumn = (header: Header<ColumnAdditionalData, unknown>) => {
    // TODO Check if this is still needed.
    // Might be required for https://work4all.atlassian.net/browse/WW-6357

    // if (!props.reordableColumns) return false;
    // if (column.disableOrder) return false;
    // Allow pinned/sticky columns to move within their pinned group
    if (header.id === SELECTION_COLUMN_ID) return false;
    return true;
  };

  let canResizeLast = false;

  const groupLevel = groupBy?.length || 0;

  return (
    <HeaderWrapperV8 className={props.classes?.headerWrapper}>
      {props.headerGroups.map((headerGroup) => {
        return (
          <HeaderRow
            className={props.classes?.headerRow}
            style={{ ...props.styles?.row /* , width: props.width */ }}
            groupLevel={groupLevel}
            key={headerGroup.id}
          >
            {additionalHeaders}
            {headerGroup.headers.map((header, index) => {
              const canResizeCurrent = header.column.getCanResize();

              const commonCellProps: HeaderCellV8Props = {
                noSeperator: props.noSeperator,
                header: header,
                classes: props.classes,
                canResizeCurrent: canResizeCurrent,
                canResizeLast: canResizeLast,
                style: props.styles?.cell,
                hideTooltip: hideHeaderTooltip,
              };

              const view = canReorderColumn(header) ? (
                <DraggableColumnHeaderWrapper
                  id={header.id}
                  index={index}
                  onHover={({ id: draggedId, index: hoverIndex }) => {
                    // Only show drop indicator if drop is allowed at this position
                    if (
                      hoverIndex !== null &&
                      isDropAllowed(draggedId, hoverIndex)
                    ) {
                      setDragState(hoverIndex);
                    } else {
                      setDragState(null);
                    }
                  }}
                  onDrop={onDragEnd}
                  onDragEnd={() => setDragState(null)}
                  canGroupBy={header.column.getCanGroup()}
                >
                  {({ dragRef, dropRef }) => {
                    return (
                      <HeaderCellV8
                        {...commonCellProps}
                        rootRef={dropRef}
                        cellRef={dragRef}
                        columnDragIndicators={{
                          before: dragState === index,
                          after: dragState === index + 1,
                        }}
                      />
                    );
                  }}
                </DraggableColumnHeaderWrapper>
              ) : (
                <HeaderCellV8 {...commonCellProps} />
              );

              canResizeLast = canResizeCurrent;
              return <React.Fragment key={header.id}>{view}</React.Fragment>;
            })}
            {additionalHeadersAfter}
          </HeaderRow>
        );
      })}
    </HeaderWrapperV8>
  );
}
